import { useQuery } from "@/api/utils/useQuery"
import { QueryResult } from "@/api/utils/types"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { getDcaAllocationConfigHistory } from "@/api/backofficeRest/generated/dca-allocation-config/dca-allocation-config"
import {
  DcaAllocationConfigHistoryResponse,
  GetDcaAllocationConfigHistoryAccountState,
  GetDcaAllocationConfigHistoryOccupier,
  GetDcaAllocationConfigResponse,
} from "@/api/backofficeRest/generated/model"
import {
  AccountState,
  IDcaAllocationCell,
  IDcaAllocationChange,
  IDcaAllocationConfigEntry,
  IDcaAllocationHistoryEntry,
  Occupier,
} from "@/models/dcaAllocation/dcaAllocation.types"

/**
 * Reads the DCA allocation config and its change history over the backoffice REST client.
 *
 * The config read is deliberately unfiltered: it returns every row including
 * zero-percentage (retired-but-present) agencies, because the screen has to show an agency
 * held at 0 in order to let anyone put it back.
 */

/**
 * The segment enums are generated once per operation, so each carries the same values as
 * the domain pair under a distinct declaration. These casts assert that equivalence in one
 * place rather than at every call site.
 */
const toHistoryOccupier = (
  occupier: Occupier,
): GetDcaAllocationConfigHistoryOccupier =>
  occupier as unknown as GetDcaAllocationConfigHistoryOccupier

const toHistoryAccountState = (
  accountState: AccountState,
): GetDcaAllocationConfigHistoryAccountState =>
  accountState as unknown as GetDcaAllocationConfigHistoryAccountState

export const configEntriesFrom = (
  raw: GetDcaAllocationConfigResponse,
): IDcaAllocationConfigEntry[] =>
  (raw.allocations ?? []).map((allocation) => ({
    occupier: allocation.occupier as unknown as Occupier,
    accountState: allocation.accountState as unknown as AccountState,
    placementNumber: allocation.placementNumber,
    dca: allocation.dca,
    percentage: allocation.percentage,
  }))

export const historyEntriesFrom = (
  raw: DcaAllocationConfigHistoryResponse,
): IDcaAllocationHistoryEntry[] =>
  (raw.historyEntries ?? []).map((entry) => ({
    changeId: entry.changeId,
    dca: entry.dca,
    // Absent is not zero: null means the agency was not in the split, while 0 means it
    // was there holding nothing. Coercing either way misreports what the change did.
    previousPercentage: entry.previousPercentage ?? null,
    newPercentage: entry.newPercentage ?? null,
    changedBy: entry.changedBy ?? null,
    changedAt: entry.changedAt,
  }))

/**
 * Groups the flat rows the endpoint returns into one cell per
 * (occupier, accountState, placementNumber).
 */
export const groupIntoCells = (
  entries: IDcaAllocationConfigEntry[],
): IDcaAllocationCell[] => {
  const cells = new Map<string, IDcaAllocationCell>()

  entries.forEach((entry) => {
    const key = `${entry.occupier}:${entry.accountState}:${entry.placementNumber}`
    const cell = cells.get(key) ?? {
      occupier: entry.occupier,
      accountState: entry.accountState,
      placementNumber: entry.placementNumber,
      allocations: [],
    }
    cell.allocations.push({ dca: entry.dca, percentage: entry.percentage })
    cells.set(key, cell)
  })

  return [...cells.values()]
}

export const findCell = (
  cells: IDcaAllocationCell[],
  occupier: Occupier,
  accountState: AccountState,
  placementNumber: number,
): IDcaAllocationCell | undefined =>
  cells.find(
    (cell) =>
      cell.occupier === occupier &&
      cell.accountState === accountState &&
      cell.placementNumber === placementNumber,
  )

/**
 * Groups history entries into one change per save, newest first.
 *
 * The endpoint returns a flat list because a save touches several agencies; the screen
 * shows one dated entry per save. Order is preserved from the response rather than
 * re-sorted here - the endpoint already sorts newest first, and re-sorting on a string
 * timestamp would be a second, divergeable opinion about ordering.
 */
export const groupIntoChanges = (
  entries: IDcaAllocationHistoryEntry[],
): IDcaAllocationChange[] => {
  const changes = new Map<string, IDcaAllocationChange>()

  entries.forEach((entry) => {
    const change = changes.get(entry.changeId) ?? {
      changeId: entry.changeId,
      changedBy: entry.changedBy,
      changedAt: entry.changedAt,
      entries: [],
    }
    change.entries.push(entry)
    changes.set(entry.changeId, change)
  })

  return [...changes.values()]
}

interface IDcaAllocationService {
  getAllocationConfig: () => QueryResult<IDcaAllocationCell[]>
}

class DcaAllocationServiceClass implements IDcaAllocationService {
  /**
   * The change history for one cell, grouped into one entry per save.
   *
   * A cell that has never been configured returns an empty list rather than a 404 -
   * never having been configured is a legitimate state, not a missing resource.
   */
  async fetchAllocationHistory(
    occupier: Occupier,
    accountState: AccountState,
    placementNumber: number,
  ): Promise<IDcaAllocationChange[]> {
    // Requested directly rather than through BackofficeRestQueries because this is fetched
    // on demand when a cell is selected, not held as a reactive query.
    const raw = await getDcaAllocationConfigHistory({
      occupier: toHistoryOccupier(occupier),
      accountState: toHistoryAccountState(accountState),
      placementNumber,
    })

    return groupIntoChanges(historyEntriesFrom(raw))
  }

  getAllocationConfig(): QueryResult<IDcaAllocationCell[]> {
    return useQuery<GetDcaAllocationConfigResponse, IDcaAllocationCell[]>({
      ...BackofficeRestQueries.dcaAllocation.config,
      useRawResponse: true,
      placeholderData: [],
      parserFn: (raw) => groupIntoCells(configEntriesFrom(raw)),
    })
  }
}

export const DcaAllocationService = new DcaAllocationServiceClass()
